import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Loader from "../../components/loader/loader";
import { toastFunction, errorToastFunction } from "../../utils/toast";
import { rememberPaymentReturn } from "../../utils/payment-return";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import axios from "axios";
import { backendURL } from "../../utils/backed-url";
import { GET_PACKAGES_QUERY } from "../../graphql/queries/packages";
import { GET_CLIENT_MINIMUM_PAYMENT } from "../../graphql/queries/payments";
import { useQuery, useLazyQuery } from "@apollo/client";
import DisplaySelect from "../display-select/display-select";
import SingleSelect from "../single-select/single-select";

// returnTo is set when the payment was started from the landing page, so the
// Ozow result page sends the payer back there instead of into the app.
const AddPayment = ({ onCancel, clientId, defaultPackage, returnTo }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      package_id: defaultPackage || null,
    },
  });

  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [requiredBalance, setRequiredBalance] = useState(0);
  const [daysProvided, setDaysProvided] = useState(0);

  const selectedPackage = watch("package_id");

  const getPackagesResponse = useQuery(GET_PACKAGES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [
    getMinimumPayment,
    { loading: minimumPaymentLoading, error: minimumPaymentError },
  ] = useLazyQuery(GET_CLIENT_MINIMUM_PAYMENT, {
    onCompleted: (data) => {
      if (data?.clientMinimumPayment) {
        setRequiredBalance(data.clientMinimumPayment.requiredBalance);
        setDaysProvided(data.clientMinimumPayment.daysProvided);
      }
    },
  });

  useEffect(() => {
    if (getPackagesResponse?.data?.packages?.items) {
      const data = getPackagesResponse.data.packages.items.map((p) => ({
        value: p.id,
        label: p.name,
      }));
      setPackages(data);

      // Set default package if passed in and not already set
      if (defaultPackage && !watch("package_id")) {
        setValue("package_id", {
          value: defaultPackage.value,
          label: defaultPackage.label,
        });
      }
    }
  }, [getPackagesResponse, defaultPackage, setValue, watch]);

  useEffect(() => {
    if (selectedPackage?.value && clientId) {
      getMinimumPayment({
        variables: {
          clientId: parseInt(clientId, 10),
          packageId: parseInt(selectedPackage.value, 10),
        },
      });
    }
  }, [selectedPackage, clientId, getMinimumPayment]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const transactionRef = `TR-${Date.now()}`;
      const paymentData = {
        amount: parseInt(data.amount),
        transaction_ref: transactionRef,
        bank_ref: `BR-${Date.now()}`,
        client_id: parseInt(clientId, 10),
        package_id: parseInt(data.package_id?.value, 10),
      };

      const response = await axios.post(
        `${backendURL}/v1/api/initiate-payment/`,
        paymentData
      );

      if (response.data.payment_url && returnTo) {
        // Same tab, so Ozow's return to the result page ends the payment where
        // it started rather than leaving this page open behind it.
        rememberPaymentReturn(transactionRef, { returnTo });
        window.location.assign(response.data.payment_url);
      } else if (response.data.payment_url) {
        window.open(response.data.payment_url, "_blank");
        toastFunction("Payment initiated successfully");
        reset();
        onCancel(); // Close the modal
      } else {
        errorToastFunction("Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errMsg =
        error.response?.data?.error || "Failed to initiate payment";
      setErrorMessage(errMsg);
      errorToastFunction(errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loading || getPackagesResponse.loading || minimumPaymentLoading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (getPackagesResponse.error || minimumPaymentError) {
    return (
      <div className="basic-modal">
        <div className="basic-modal__header">
          <h3>Error Loading Data</h3>
        </div>
        <div className="basic-modal__body">
          <div
            className="input-container__error"
            style={{
              padding: "20px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {getPackagesResponse.error?.message ||
              minimumPaymentError?.message ||
              "Failed to load payment data. Please try again."}
          </div>
          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>Add Payment</h3>
      </div>
      <div className="basic-modal__sub-header">Minimum Required payment</div>
      <div className="basic-modal__sub-header-text">
        R{requiredBalance.toFixed(2)} for {daysProvided} days
      </div>
      <div className="basic-modal__body">
        {errorMessage && (
          <div
            className="input-container__error"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"amount"}
            register={register}
            rules={{
              required: "Amount is required",
              min: {
                value: 0.01,
                message: "Minimum amount is R0.01",
              },
              max: {
                value: 100000,
                message: "Maximum amount is R100,000",
              },
            }}
            placeholder={"Amount"}
            errors={errors}
          />

          <div className="input-container">
            <DisplaySelect
              placeholder={"Package"}
              name={"package_id"}
              errors={errors}
            >
              <Controller
                name={"package_id"}
                rules={{
                  required: "The Package field is required.",
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect field={field} options={packages} />
                )}
              />
            </DisplaySelect>
          </div>

          <div className="basic-modal__footer">
            <button
              type="button"
              onClick={onCancel}
              className="button button--secondary"
            >
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              Proceed to Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPayment;
